/**
 * Merge a Task's progress Communications with its care-time Observations
 * into one chronological timeline, deduping the care-time entries that were
 * tee'd from a Communication already in the list.
 *
 * Care/outreach progress logs write a Communication AND tee a care-time
 * Observation linked back via `derivedFrom`; those minutes fold into the
 * Communication row (no duplicate). Administrative progress and standalone
 * quick-adds have no Communication, so their Observations render as their own
 * rows. Legacy tee'd Observations written before the `derivedFrom` link may
 * appear as standalone rows (accepted).
 *
 * @agentic connections
 */

import type { Communication, Observation } from "@medplum/fhirtypes";

/** A single entry in the merged progress + care-time timeline. @agentic connections */
export type ProgressTimelineEntry =
  | {
      kind: "communication";
      /** Sort key (ms since epoch); 0 when no parseable timestamp. */
      at: number;
      communication: Communication;
      /** Folded-in care-time minutes from linked Observation(s), if any. */
      minutes?: number;
    }
  | {
      kind: "care-time";
      at: number;
      observation: Observation;
      minutes: number;
    };

const parseMs = (iso: string | undefined): number => {
  if (!iso) return 0;
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : 0;
};

const observationTimestamp = (obs: Observation): string | undefined =>
  obs.effectiveDateTime ?? obs.effectivePeriod?.start ?? obs.issued;

const communicationIdFromDerivedFrom = (
  obs: Observation,
): string | undefined => {
  const ref = obs.derivedFrom?.find((r) =>
    r.reference?.startsWith("Communication/"),
  )?.reference;
  return ref?.startsWith("Communication/")
    ? ref.slice("Communication/".length)
    : undefined;
};

/**
 * Build a chronological (newest-first) merged timeline of progress
 * Communications and care-time Observations.
 * @agentic connections
 */
export const buildProgressTimeline = (
  communications: Communication[],
  careTimeLogs: Observation[],
): ProgressTimelineEntry[] => {
  const commIds = new Set(
    communications.map((c) => c.id).filter((id): id is string => Boolean(id)),
  );

  // Sum tee'd minutes per Communication so the folded chip reflects all
  // Observations derived from that Communication.
  const foldedMinutesByComm = new Map<string, number>();
  const standaloneObs: Observation[] = [];
  for (const obs of careTimeLogs) {
    const parentCommId = communicationIdFromDerivedFrom(obs);
    if (parentCommId && commIds.has(parentCommId)) {
      const minutes = obs.valueQuantity?.value ?? 0;
      foldedMinutesByComm.set(
        parentCommId,
        (foldedMinutesByComm.get(parentCommId) ?? 0) + minutes,
      );
    } else {
      standaloneObs.push(obs);
    }
  }

  const entries: ProgressTimelineEntry[] = [];

  for (const communication of communications) {
    const minutes = communication.id
      ? foldedMinutesByComm.get(communication.id)
      : undefined;
    entries.push({
      kind: "communication",
      at: parseMs(communication.sent),
      communication,
      minutes: minutes && minutes > 0 ? minutes : undefined,
    });
  }

  for (const observation of standaloneObs) {
    entries.push({
      kind: "care-time",
      at: parseMs(observationTimestamp(observation)),
      observation,
      minutes: observation.valueQuantity?.value ?? 0,
    });
  }

  return entries.sort((a, b) => b.at - a.at);
};
